<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>
      <v-row class="ma-0 mb-3">
        <h2>{{title}}</h2>
        <router-link :to="'/admin/dogs'" target="_blank">
          <v-btn v-if="!useForSelection" class="ml-2" color="primary">+ New Dog</v-btn>
        </router-link>
        <v-spacer></v-spacer>
        <v-btn color="primary" v-on:click="applyFilter">Apply Filters</v-btn>&nbsp;
        <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
      </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:load="load"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert width="100%" class="mr-5 ml-5" v-model="isError" dismissible type="error"> {{ errorMessage }}</v-alert>
    </v-row>
    <v-data-table
      dense
      disable-sort
      single-select
      hide-default-footer
      class="elevation-1"
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
      :footer-props="footerProps"
      :show-select="useForSelection">
      <template v-slot:item.data-table-select="{ item }">      
        <v-btn v-if="useForSelection" class="mt-2" icon v-on:click="() => onRowSelected(item.dogId)" color="primary">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <template v-slot:header.dogId="{ header }">
        <v-text-field
          hide-details
          v-model="searchParams.dogId"
          label="Dog ID"
          clearable
          @click:clear="searchParams.dogId = ''"
          v-on:keyup.enter="applyFilter"
        />&nbsp;
        <v-text-field
          hide-details
          class="mt-0 pt-0 mb-2"
          v-model="searchParams.name"
          label="Dog Name"
          clearable
          @click:clear="searchParams.name = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.litterId="{ header }">
        <v-text-field
          hide-details
          v-model="searchParams.litterId"
          label="Litter ID"
          clearable
          @click:clear="searchParams.damId = ''"
          v-on:keyup.enter="applyFilter"
        />&nbsp;
        <v-text-field
          hide-details
          class="mt-0 pt-0 mb-2"
          v-model="searchParams.damName"
          label="Dam Name/ID"
          clearable
          @click:clear="searchParams.damName = ''"
          v-on:keyup.enter="applyFilter"
        /> 
        <v-text-field
          hide-details
          class="mt-0 pt-0 mb-2"
          v-model="searchParams.sireName"
          label="Sire Name/ID"
          clearable
          @click:clear="searchParams.sireName = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.owners="{ header }">
        <div>
          <v-text-field
            v-model="searchParams.ownerName"
            label="Owner/Co-Owner Name"
            clearable
            @click:clear="searchParams.ownerName = ''"
            v-on:keyup.enter="applyFilter"
          />
        </div>
      </template>
      <template v-slot:header.isAustralianShepherd="{ header }">
        <div>
          <v-checkbox
            :indeterminate="searchParams.isASCA == null"
            v-model="searchParams.isASCA"
            label="Is ASCA"
            v-on:change="toggleAscaCheckbox"
          />
        </div>
      </template>
      <template v-slot:header.whelpingDate="{ header }">
        <div width="300px">
          <datepicker
            :date="searchParams.whelpingDateFrom"
            label="Whelping Date From"
            v-on:updateDates="updateDates($event, 'whelpingDateFrom')"
            v-on:enter="updateDateSearch($event, 'whelpingDateFrom')"
          />
          <datepicker
            :date="searchParams.whelpingDateTo"
            label="Whelping Date To"
            v-on:updateDates="updateDates($event, 'whelpingDateTo')"
            v-on:enter="updateDateSearch($event, 'whelpingDateTo')"
          />
        </div>
      </template>
      <template v-slot:item.dogId="{ item }">
        <router-link :to="`/admin/dogs/${encodeURIComponent(item.dogId.trim())}`" target="_blank">
          {{ item.dogId }} - {{ item.name }}
        </router-link>
      </template>
      <template v-slot:item.litterId="{ item }">
        Litter: 
        <router-link v-if="item.litterId" :to="`/admin/litters/${item.litterId}`" target="_blank">
          {{ item.litterId }} 
        </router-link>
        <span v-else>
          N/A
        </span>  
        <br/>
        Dam: <router-link v-if="item.damId && item.damName" :to="`/admin/dogs/${encodeURIComponent(item.damId.trim())}`" target="_blank">
          {{ item.damId }} - {{ item.damName }} 
        </router-link>
        <span v-else>
          N/A
        </span><br/>
        Sire: <router-link v-if="item.sireId && item.sireName" :to="`/admin/dogs/${encodeURIComponent(item.sireId.trim())}`" target="_blank">
          {{ item.sireId }} - {{ item.sireName }}
        </router-link>
        <span v-else>
          N/A
        </span>  
      </template>
      <template v-slot:item.whelpingDate="{ item }">
        <localized-date v-if="item.whelpingDate" :date="item.whelpingDate" />
      </template>
      <template v-slot:item.owners="{ item }">
        <div class="pl-5">
          <CoownersList
            :owners="item.owners"
            :ownershipType="dogOwnershipType"
            :hideHeader="true"
          />
        </div>
      </template>
      <template v-slot:item.registrationId="{ item }">
        <p class="mt-3">
          <a
            v-if="!!item.registrationId"
            v-on:click="showRegistration(item.registrationId)"
            >View</a
          >
          <br />
        </p>
      </template>
      <template v-slot:item.registrationDate="{ item }">
        <localized-date v-if="item.whelpingDate" :date="item.registrationDate" />
      </template>
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import CoownersList, {
  OwnershipType,
} from "../../common/CoownersList.vue";
import Datepicker from "../../common/inputs/DatepickerFormatted.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";

import dogService from "@/services/admin/dogService";
import { getErrorMessage } from "@/services/errorHandler";

import { UserInfo } from "@/types/User";
import { MetaNames } from "@/constants";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";

type WhelpingDateFilter = "whelpingDateFrom" | "whelpingDateTo";

export default Vue.extend({
  name: "DogList",
  components: {
    CoownersList,
    Datepicker,
    PaginationSelect
  },
  props: {
    useForSelection: { type: Boolean},
    onRowSelected: { type: Function },
  },
  data: function() {
    return {
      title: MetaNames.AdminDogs,
      loading: false,
      isError: false,
      errorMessage: "",
      paginatedResponse: {} as PaginatedResponse<object>,
      searchParams: {
        dogId: "",
        name: "",
        isASCA: null as any,
        damName: "",
        sireName: "",
        ascending: false,
        ownerName: "",
        whelpingDateFrom: null as Temporal.PlainDate | null,
        whelpingDateTo: null as Temporal.PlainDate | null,
      },
      options: {} as PaginationOptions,
      headers: [
        { text: "", align: "left", value: "dogId", width: "25%" },
        { text: "", align: "left", value: "litterId", width: "25%" },
        { text: "", align: "center", value: "whelpingDate", width: "15%" },
        { text: "", align: "left", value: "owners",width: "25%" },
        { text: "", align: "left", value: "isAustralianShepherd", width: "5%"},
        { text: "Registration date", align: "center", value: "registrationDate", width: "5%" },        
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50, 100] },
      search: "",
      currentIsAscaIdx: 0,
      menu: false,
      dates: [] as string[],
    };
  },
  created: function() {
    this.load();
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    }
  },
  methods: {
    toggleAscaCheckbox: function() {
      var states = [null, true, false];
      var next = (++this.currentIsAscaIdx) % states.length;
      this.currentIsAscaIdx = next;
      this.searchParams.isASCA = states[next];
    },
    clearAllFilters: function() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.dates = [];
      this.searchParams = {
        dogId: "",
        name:"",
        isASCA:null,
        damName:"",
        sireName:"",
        ascending: false,
        ownerName: "",
        whelpingDateFrom: null,
        whelpingDateTo: null,
      };
    },
    updateDates: function (val: { date: Temporal.PlainDate | null }, key: WhelpingDateFilter) {
      this.searchParams[key] = val.date;
    },
    updateDateSearch: async function(val: { date: Temporal.PlainDate | null }, key: WhelpingDateFilter) {
      await this.updateDates(val, key);
      this.applyFilter();
    },
    load: async function () {
      this.loading = true;

      const {
        page,
        itemsPerPage,
      } = this.options;

      try {
        const response = await dogService.getDogsList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;
        this.isError = true;

        this.errorMessage = getErrorMessage(ex);
      }
    },
    applyFilter() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;

      this.load();
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    dogOwnershipType: function() {
      return OwnershipType.Dog;
    },
  },
});
</script>
